import axios from "./axiosInstance";

export const fetchAllOrders = async (page, perPage, filter, Nested) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.get(
      `get-orders?perPage=${perPage}&page=${page}&name=${filter}${Nested}`,
    );
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const fetchOrderFullfillmentBillingStatusHistory = async (
  page,
  perPage,
  filter,
  id,
) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.get(
      `order/get-fullfillment-billing-status-history/${id}?perPage=${perPage}&page=${page}&name=${filter}`,
    );
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const fetchSingleOrder = async (id) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.get(`order/get-by-id/${id}`);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

// The API's limit per order/get-by-ids request.
const ORDERS_PER_REQUEST = 100;
// Errors the one-by-one way can't get round.
const FINAL_STATUSES = [401, 403, 422, 429];

const ordersInOrder = (ids, orders) => {
  const byId = new Map(orders.map((order) => [String(order.id), order]));
  return {
    data: ids.map((id) => byId.get(String(id))).filter(Boolean),
    missing: ids.filter((id) => !byId.has(String(id))),
    error: null,
  };
};

// One order a request, 4 at a time: for an API without order/get-by-ids. An
// order that's gone (404) or not the user's (403) is just missing; any other
// failure is the `error`.
const fetchOneByOne = async (ids) => {
  const responses = [];
  for (let at = 0; at < ids.length; at += 4) {
    // eslint-disable-next-line no-await-in-loop
    const part = await Promise.all(
      ids.slice(at, at + 4).map((id) => fetchSingleOrder(id)),
    );
    responses.push(...part);
  }
  const found = responses
    .map((response) => (response.error ? null : response?.data?.result))
    .filter((order) => order?.id != null);
  const failed = responses.find(
    (response) =>
      response.error && ![403, 404].includes(response.error?.response?.status),
  );
  return { ...ordersInOrder(ids, found), error: failed ? failed.error : null };
};

/**
 * Several orders in one request (100 a request), each as fetchSingleOrder
 * returns it. Resolves to { data, missing, error }: `data` in the order
 * asked; `missing` the ids not in it, orders that were deleted or aren't the
 * user's to see; `error` when some couldn't be asked for at all (then
 * `missing` may hold orders that exist). An API that can't answer it (an
 * older backend) is asked one order at a time instead.
 *
 * `palletHistory: false` leaves out each pallet's history, for screens that
 * don't show it: on a busy product it's most of an order's size.
 */
export const fetchOrdersByIds = async (
  ids = [],
  { palletHistory = true } = {},
) => {
  const wanted = [];
  ids.forEach((id) => {
    if (id != null && !wanted.some((known) => String(known) === String(id)))
      wanted.push(id);
  });
  if (!wanted.length) return { data: [], missing: [], error: null };
  try {
    const orders = [];
    for (let at = 0; at < wanted.length; at += ORDERS_PER_REQUEST) {
      // eslint-disable-next-line no-await-in-loop
      const res = await axios.post(`order/get-by-ids`, {
        ids: wanted.slice(at, at + ORDERS_PER_REQUEST).map(Number),
        ...(palletHistory ? {} : { pallet_history: false }),
      });
      const part = res?.data?.result;
      orders.push(...(Array.isArray(part) ? part : []));
    }
    return ordersInOrder(wanted, orders);
  } catch (error) {
    if (FINAL_STATUSES.includes(error?.response?.status)) {
      return { data: [], missing: wanted, error };
    }
    return fetchOneByOne(wanted);
  }
};

export const fetchShippingCompanies = async () => {
  let response = { data: null, error: null };
  try {
    const res = await axios.get(`user-shipping-companies/manual`);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const addShippingCompany = async (data) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(`user-shipping-companies/manual/store`, data);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const fetchOrderStatuses = async () => {
  let response = { data: null, error: null };
  try {
    const res = await axios.get(`userWarehouse/get-order-statuses`);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const fetchCostType = async () => {
  let response = { data: null, error: null };
  try {
    const res = await axios.get(`cost-type/get`);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const storeOrder = async (data) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(`order/store`, data);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const updateOrder = async (data, id) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(`order/update/${id}`, data);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const addCostType = async (data) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(`cost-type/store`, data);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const storeAdditionlCost = async (data, id) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(`order/store-additional-cost/${id}`, data);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

// `config` is passed to axios, e.g. { onUploadProgress } (helpers/uploads).
export const storeOrderChange = async (data, order, status, config) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(
      `order/update-status/${order}/${status}`,
      data,
      config,
    );
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

// Sets the orders' fulfilment status to Archived. The answer lists
// archived_order_ids, already_archived_order_ids and skipped_order_ids.
export const bulkArchiveOrders = async (orderIds) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(`order/bulk-archive`, {
      order_ids: orderIds,
    });
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

// Settings → Auto-archive orders: the setting, and how many orders it would
// archive now (orders older than `days`, or than the saved days).
export const fetchAutoArchive = async (days) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.get(`order/auto-archive`, {
      params: days ? { days } : {},
    });
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

// "Archive now": archives them straight away (older than `days`, or than the
// saved days).
export const runAutoArchive = async (days) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(
      `order/auto-archive/run`,
      days ? { days } : {},
    );
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

// Brings archived orders back to the Orders list (clears their archive mark;
// nothing else changes).
export const bulkUnarchiveOrders = async (orderIds) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(`order/bulk-unarchive`, {
      order_ids: orderIds,
    });
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const deleteSingleOrder = async (id) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.delete(`order/delete/${id}`);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const fetchOrderNotes = async (orderId) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.get(`/order/order-notes/${orderId}`);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

// `config` is passed to axios, e.g. { onUploadProgress } (helpers/uploads).
export const createOrderNotes = async (orderId, data, config) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(
      `order/store-general-notes/${orderId}`,
      data,
      config,
    );
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const updateFullfilmentBillingStatus = async (
  orderId,
  status,
  notes,
) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(
      `order/update-fulfillment-billing-status/${orderId}/${status}`,
      { notes: notes },
    );
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const fetchOrderAttachments = async (orderId) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.get(`order/order-notes/${orderId}`);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const importOrders = async (formData) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(`order/import-orders`, formData);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

export const fetchOrderItems = async (id) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.get(`order/order-items/${id}`);
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};
